import React, {
	ReactNode,
	createContext,
	useEffect,
	useMemo,
	useRef,
	ChangeEvent,
	Provider,
	ReactElement,
	useState,
} from 'react';
import { useDispatch, useSelector as useSel } from 'react-redux';
import {ContentServiceJobStatus, FkTable, GeneratedContent as GC} from '../../stores/types/content';
import { GreenroomState as GS } from '../../stores/types/types';
import { lockedAssetFieldsMedia as LAFM, lockedAssetFieldsOverlay as LAFO } from '../../components/style-packs/style-pack-consts';
import { STATIC_ASSET_URL, contentTemplateStyles } from '../../utils/content-templates';
import { upsertContent } from '../../stores/actions/generate-content';
import { getNumberedFieldName } from '../../components/general-components/input/gr-picture-input';
import useProcessingToaster from '../../hooks/content/use-processing';
import { reorderPictures } from '../../hooks/content/use-drag-content';
import { setShotModalLocked } from '../../stores/actions/shots';
import usePrevious from '../../utils/use-previous';
import { Field, FieldType, FieldValue, InputTypeByCapsuleType, maxPictureCount, StylePackContentType, ContentTemplate } from '../../components/types/stylepacks';
import ChipProvider from './content-chip-context';
import { HangingUserKey } from '../../stores/reducers/generate-content';
import { ColorMapping, Theme, ThemeMaps } from '../../stores/types/themes';
import { attachTheme } from '../../stores/actions/themes';
import { hexToRgb } from '../../utils/utils';
import useMediaItemMemos from '../../hooks/use-media-memos';
import useDebouncedCallback from '../../hooks/use-debounce-callback';
import { LoadThemeMaps } from '../../connection/themes';

type InputChangeEvent = ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>

type ContentGeneratorContextType = {
	statefulFormValues: Record<string, FieldValue>;
	selectedGraphicApiShape: Field[];
	currTemplate: ContentTemplate;
	UpdatingToaster: ReactElement<any, Provider<ContentGeneratorContextType>>;
	removePictureWithCount: (field: Field, count: number) => void;
	addNewPicture: (field: Field) => void;
	defaultTitleOverlayData: any;
	onDragEnd: (field: Field, position: number, count: number) => void;
	onChange: (e: ChangeEvent<HTMLInputElement>) => void;
	uniqueId: string;
	hasText: boolean;
	applyThemeToItem: (theme: Partial<Theme>) => void;
	currrentThemeId: number;
	getTemplateSpecificStyling: (template: ContentTemplate) => ColorMapping | null;
};

export const FormContext = createContext<ContentGeneratorContextType>({
	statefulFormValues: {},
	selectedGraphicApiShape: [],
	currTemplate: {} as unknown as ContentTemplate,
	UpdatingToaster: <></>,
	removePictureWithCount: () => {},
	addNewPicture: () => {},
	onDragEnd: () => {},
	defaultTitleOverlayData: {},
	onChange: () => {},
	uniqueId: 'no-id',
	hasText: false,
	applyThemeToItem: () => {},
	currrentThemeId: 0,
	getTemplateSpecificStyling: () => null,
});

type ReturnValue = ReactElement<any, Provider<ContentGeneratorContextType>>

type Props = {
	children: ReactNode
	isForShotEditorModal?: boolean;
}


const hasTextError = (
    formValues: any, // [{key: userData, value: any}]
    template: ContentTemplate
) => {
	if (!template) return false;
	
	const selectedGraphicApiShape = template.data?.variables;
	if (template.contentType !== StylePackContentType.TITLE || !template.contentType) {
		// only do this for titles at the moment
		return false;
	}
    try {
        let errorNoText = true;

		if (selectedGraphicApiShape) {
			for (const field of selectedGraphicApiShape) {
				if (
					InputTypeByCapsuleType[(field.type as FieldType)] === 'text'
					&& formValues[field.userData] !== ''
				) {
					errorNoText = false;
					break;
				}
			}
		}
        return errorNoText;
    } catch (e) {
        return false;
    }
}

const shownFormFields = (field: {userData: string}) => !(
	field.userData === 'fields'
	|| field.userData  === 'compScale'
);


// a context to hold the state and ref versions of the form outside of the component,
// so that it doesn't lose its value when unrendering the component
// could move more things into this from both title-content.tsx and content-generator-handler.tsx
// as those two components are basically acting as contexts themselves
// however, this was already a big refactor, so I'm leaving it for now
export default function ContentFormProvider(props: Props): ReturnValue {
	const dispatch = useDispatch();
	const { isForShotEditorModal = false } = props;


	const {
		currentGraphicId,
		graphics: overlays,
		shotModalGraphics: overlayDrafts,
	} = useSel((s: GS) => s.GraphicsReducer);

	const {
		contentTemplates,
		generatedContent,
		generatedContentEdits,
	} = useSel((s: GS) => s.GeneratedContentReducer);

	const {
		currentTemplateMediaItem,
		currentTemplate: selectedMediaTemplate,
	} = useMediaItemMemos();

	const token = useSel((s: GS) => s.SessionReducer.token);
	const meetingId = useSel((s: GS) => s.SessionReducer.meeting?.meeting);
	const channelId = useSel((s: GS) => s.SessionReducer.meeting?.channel);
	const userId = useSel((s: GS) => s.SessionReducer.user?.gr_user);
	const mediaDrafts = useSel((s: GS) => s.MediaReducer.shotModalMedia);
	const themeMaps = useSel((s: GS) => s.ThemesReducer.themeMaps);
	const attachedThemes = useSel((s: GS) => s.ThemesReducer.attachedThemes);

	const formSubmitDataRef = useRef<Record<string, any>>({});
	const [statefulFormValues, setStatefulFormValues] = useState<Record<string, FieldValue>>({});

	const timmyRef = useRef<NodeJS.Timeout>();

	const usedOverlays = useMemo(
		() => (isForShotEditorModal ? overlayDrafts : overlays),
		[isForShotEditorModal, overlayDrafts, overlays],
	);

	const selectedGraphic = useMemo(
		() => usedOverlays.find((g) => g.id === currentGraphicId),
		[currentGraphicId, usedOverlays],
	);

	const selectedMediaItem = useMemo(
		() => (
			isForShotEditorModal 
				? mediaDrafts.find(m => m.id === currentTemplateMediaItem?.id) 
				: currentTemplateMediaItem
		),
		[isForShotEditorModal, mediaDrafts, currentTemplateMediaItem],
	);

	const [associatedItem, fkTable, lockedAssetFields] = useMemo(
		() => (selectedGraphic 
			? [selectedGraphic, FkTable.graphics, LAFO] 
			: [selectedMediaItem, FkTable.media, LAFM]),
		[selectedGraphic, selectedMediaItem],
	);

	const currTemplate = useMemo(() => {
		let filteredTemplates: ContentTemplate[] = [];
		if (associatedItem && fkTable === FkTable.graphics) {
			// prefer selected graphic
			filteredTemplates = contentTemplates.filter((t) => (t.type === associatedItem.template_type));
		} else if (fkTable === FkTable.media && selectedMediaTemplate) {
			// then try to find by media template
			filteredTemplates = contentTemplates.filter((t) => (t.type === selectedMediaTemplate.type));
		} else if (associatedItem) {
			filteredTemplates = contentTemplates.filter((t) => (t.type === associatedItem.template_type));
		}

		// a variation of a template may have the same type, but a different url
		const templateVariation = filteredTemplates.find((t) => {
			const videoUrl = t?.videoUrl?.replace('ASSET_URL', STATIC_ASSET_URL);
			return !!videoUrl && associatedItem?.images?.includes(videoUrl);
		});

		// the bts segment/walkup share a template key but have different shown fields/defaults
		if (associatedItem?.template_type === 'api-brandlive-bts-segment-01') {
			const override = associatedItem?.original_name?.includes('Walkup')
				? filteredTemplates.filter((t) => t.contentType === 'walkup')[0]
				: filteredTemplates.filter((t) => t.contentType === 'segment')[0];

			return override ?? templateVariation ?? filteredTemplates[0];
		}

		return templateVariation ?? filteredTemplates[0];
	}, [
		!!selectedMediaTemplate,
		selectedMediaTemplate,
		contentTemplates,
		associatedItem?.template_type,
		associatedItem?.images,
		fkTable
	]);

	const templateHasVariables = useMemo(
		() => (currTemplate?.data?.variables?.length ?? 0) > 0,
		[currTemplate],
	);

	const generatedContentItem = useMemo(() => {
		const cleanFkId = fkTable === FkTable.media ? selectedMediaItem?.id : selectedGraphic?.id;

		const backupContent: GC = {
			templateId: currTemplate?.id,
			provider: currTemplate?.provider,
			type: currTemplate?.type,
			fkTable,
			fkId: cleanFkId,
			themeId: currTemplate?.themeId,
			data: [],
			version: 1,
		};

		const match = (gc: GC) => (gc.fkTable === fkTable && gc.fkId === cleanFkId);

		const draftGenContent = generatedContentEdits?.find(match);

		if (isForShotEditorModal && draftGenContent) {
			return draftGenContent;
		}

		return generatedContent?.find(match) ?? backupContent;
	}, [
		isForShotEditorModal,
		generatedContent,
		generatedContentEdits,
		currTemplate,
		selectedMediaTemplate,
		selectedGraphic?.id,
		selectedMediaItem?.id,
		fkTable
	]);

	const selectedGraphicApiShape: Field[] = useMemo(() => (
		(templateHasVariables && currTemplate 
			? currTemplate?.data?.variables 
			: lockedAssetFields
		)?.filter(shownFormFields)), 
	[currTemplate, templateHasVariables]);

	const uniqueId = useMemo(
		() =>  HangingUserKey(generatedContentItem.fkTable, associatedItem?.id),
		[selectedMediaTemplate, associatedItem, generatedContentItem],
	);

	const prevUniqueId = usePrevious(uniqueId);

	const isDirty = useRef<Map<string, Map<string, any>>>(new Map());

	// for internally setting the form, invoked by init, user input change, or context functions
	const hardSetFormValuesRefAndUpdateStateValue = (newValues: Record<string, any>) => {
		formSubmitDataRef.current[uniqueId] = newValues;
		setStatefulFormValues(formSubmitDataRef.current[uniqueId]);
	};

	// a user event has changed an input and we need to update our form
	const handleInputChange = (event: InputChangeEvent) => {
		const { name, value } = event.target;
		formSubmitDataRef.current[uniqueId][name] = value;

		if (!isDirty.current?.get(uniqueId)) {
			isDirty.current?.set(uniqueId, new Map());
		}

		isDirty.current?.get(uniqueId)?.set(name, value);

		hardSetFormValuesRefAndUpdateStateValue(formSubmitDataRef.current[uniqueId]);
	};

	const defaultTitleOverlayData = useMemo(() => {
		const url = currTemplate?.videoUrl?.replace('ASSET_URL', STATIC_ASSET_URL);
		return contentTemplateStyles.find((style) => style.url === url);
	}, [generatedContentItem, currTemplate]);

	const [hasText, setHasText] = useState(false);
	useEffect(() => {
		setHasText(!hasTextError(statefulFormValues, currTemplate));
	}, [statefulFormValues, currTemplate?.data?.variables])

	const handleFormSubmit = (wUniqueId: string, initialChipUser?: number, themeVersion?: number) => {
		if (!token || !meetingId || !userId || !associatedItem?.id || !generatedContentItem) {
			return;
		}

		const usedFormVals = formSubmitDataRef.current[wUniqueId];
		const textError = hasTextError(usedFormVals, currTemplate);
		setHasText(!textError);
		if (textError) {
			console.error('NO FORM TEXT')
			return;
		}
		
		// eslint-disable-next-line no-template-curly-in-string
		const keysToIgnore = ['speaker${i}-headshot', 'speaker${i}-name', 'speaker${i}-company', 'speaker${i}-position'];
		const keys = Object.keys(usedFormVals).filter((key) => !keysToIgnore.includes(key));

		const data = keys.map((key) => ({
			key,
			value: usedFormVals[key],
		}));

		data.push(...[{ key: 'compScale', value: '65' }, { key: 'duration', value: currTemplate?.data?.duration?.toString() ?? '8' }]);

		if (currTemplate?.type === 'brandlive-walkups') {
			data.push({
				key: 'eventName',
				value: formSubmitDataRef.current[wUniqueId].eventName ?? 'Brandlive Studios',
			});
		}

		// had to set the outgoing version to a timestamp because the UI was sending a stale
		// version in the shot edit modal, and the backend was rejecting it
		const thisVersion = new Date().valueOf();
		const newGeneratedContent = {
			...generatedContentItem,
			fkId: associatedItem?.id,
			data,
			jobStatus: ContentServiceJobStatus.processing,
			version: thisVersion,
			contentUrl: associatedItem?.id 
				? associatedItem.thumbnail_url 
				: currTemplate?.data?.thumbnail.url
		};

		if (themeVersion) {
			newGeneratedContent.currentThemeVersion = themeVersion;
		}

		if (currTemplate?.data?.apiVersion) {
			newGeneratedContent.apiVersion = currTemplate.data.apiVersion;
		}
		// new v3 of capsule needs a config to be passed through
		if (currTemplate?.data?.config) {
			newGeneratedContent.config= currTemplate.data.config;
		}

		dispatch(setShotModalLocked(newGeneratedContent.version));
		dispatch(upsertContent(
			token,
			meetingId,
			userId,
			associatedItem,
			newGeneratedContent,
			initialChipUser
		));
	};

	const addNewPicture = (field: Field) => {
		const currentValue = Number(generatedContentItem?.data?.find((d) => d.key === 'pictureCount')?.value
			?? formSubmitDataRef.current[uniqueId]?.pictureCount ?? 1);
		const newValue = currentValue + 1;

		if (newValue > maxPictureCount) {
			return;
		}

		const event = {
			target: {
				name: 'pictureCount',
				value: newValue.toString(),
			},
		} as ChangeEvent<HTMLInputElement>;

		handleInputChange(event);

		const parentFieldName = getNumberedFieldName(field.userData, newValue);
		const defaultParentFieldName = getNumberedFieldName(field.userData, 2);

		const parentValue = {
			target: {
				name: parentFieldName,
				value: defaultTitleOverlayData?.default?.[defaultParentFieldName] ?? '',
			},
		};

		handleInputChange(parentValue as ChangeEvent<HTMLInputElement>);

		field.variables.forEach((variable: any) => {
			const formattedFieldValueName = getNumberedFieldName(variable.userData, 2);
			const formattedFieldName = getNumberedFieldName(variable.userData, newValue);
			const subEvent = {
				target: {
					name: formattedFieldName,
					value: defaultTitleOverlayData?.default?.[formattedFieldValueName] ?? '',
				},
			} as ChangeEvent<HTMLInputElement>;
			handleInputChange(subEvent);
		});

		handleFormSubmit(uniqueId);
	};

	const removePictureWithCount = (field: Field, count: number) => {
		const pictureCount = Number(formSubmitDataRef.current[uniqueId]?.pictureCount ?? 1);
		// we need one picture at least
		if (pictureCount === 1) {
			return;
		}

		const parentFieldName = getNumberedFieldName(field.userData, count);
		const childFieldNames: string[] = field.variables.map(
			(variable: any) => getNumberedFieldName(variable.userData, count),
		);

		const currentValues = { ...formSubmitDataRef.current[uniqueId] };

		const cleanUpValues = (parent: string, children: string[]) => {
			delete currentValues[parent];
			children.forEach((child) => {
				delete currentValues[child];
			});
		};

		// remove current values
		cleanUpValues(parentFieldName, childFieldNames);

		// update and replace data
		for (let i = count + 1; i <= pictureCount; i += 1) {
			const replaceParent = getNumberedFieldName(field.userData, i - 1);
			const replaceChildren = field.variables.map(
				(variable: any) => getNumberedFieldName(variable.userData, i - 1),
			);

			const currentParent = getNumberedFieldName(field.userData, i);
			const currentChildren = field.variables.map(
				(variable: any) => getNumberedFieldName(variable.userData, i),
			);

			// replace current values
			currentValues[replaceParent] = currentValues[currentParent];
			currentChildren.forEach((child: string, index: number) => {
				currentValues[replaceChildren[index]] = currentValues[child];
			});

			// remove current values
			cleanUpValues(currentParent, currentChildren);

			// update the pictureCount
			currentValues.pictureCount = (pictureCount - 1).toString();

			// save updated values
			hardSetFormValuesRefAndUpdateStateValue(currentValues);
			handleFormSubmit(uniqueId);
		}
	};

	const onDragEnd = (field: Field, position: number, count: number) => {
		const newValues = reorderPictures(field, formSubmitDataRef.current[uniqueId], position, count);
		hardSetFormValuesRefAndUpdateStateValue(newValues);
		handleFormSubmit(uniqueId);
	};

	const {
		debounceProcessingToast,
		UpdatingToaster,
	} = useProcessingToaster(
		generatedContentItem?.jobStatus,
		generatedContentItem?.id,
		generatedContentItem?.fkTable === FkTable.media,
	);

	const onChange = (
		e?: ChangeEvent<HTMLInputElement>,
		initialChipUser?: number,
		themeVersion?: number,
	) => {
		if (e) handleInputChange(e);
		clearTimeout(timmyRef.current);
		debounceProcessingToast();
		if (associatedItem) {
			timmyRef.current = setTimeout(() => handleFormSubmit(
				uniqueId,
				initialChipUser,
				themeVersion,
			), 800);
		}
	};

	// template can be null ('a:1-g:1-t:null') when loading
	const canShow = useMemo(() => (
		uniqueId.includes('null') === false
		&& selectedGraphicApiShape
		&& associatedItem
		&& currTemplate
		&& statefulFormValues
	), [
		selectedGraphicApiShape,
		associatedItem,
		currTemplate,
		statefulFormValues,
		uniqueId,
	]);

	// determine value of field from existing
	const getField = (eData: Record<string, FieldValue>, field: Field, label: string) => {
		const value = eData?.[label]
			?? defaultTitleOverlayData?.default?.[label]
			?? field.default;
		if (field.type === 'select') return value;
		return value?.toString() ?? '';
	};

	const getTemplateSpecificStyling = (template: ContentTemplate) => {
		const key = `${template.type}:${template.contentType}`;
		const themesForItem = themeMaps?.[key];
		if (!themesForItem) return null;
		return themesForItem ?? null;
	}

	const applyThemeToItem = useDebouncedCallback((theme: Partial<Theme>) => {
		if (!theme.colors || theme.colors.length === 0) {
			console.error('Theme error', theme);
			return;
		}
		
		if (theme.id && theme.id !== generatedContentItem.themeId) {
			generatedContentItem.themeId = theme.id;
			dispatch(attachTheme(uniqueId, theme.id));
		}
		
		const themeSpecificMap = getTemplateSpecificStyling(currTemplate);

		const colorFields = selectedGraphicApiShape.filter((field) => field.type === 'color');
		let i = 0;
		if (themeSpecificMap) {
			for (i; i < colorFields.length; i++) {
				const name = colorFields[i].userData;
				// index of the color in the theme for this specific template key
				const colorIndex = themeSpecificMap.fieldToColorIndex[name];
				const safeColor = theme.colors[colorIndex] ?? '#000000';
				const opacity = themeSpecificMap.fieldToOpacity[name];
				const value: string | number[] = opacity ? [...hexToRgb(safeColor), opacity] : safeColor;
				
				if (
					(Array.isArray(value) && value.find((n) => Number.isNaN(n)))
					|| (!colorIndex && colorIndex !== 0)
				) return;

				onChange(
					{target: {
						name,
						value
					}} as ChangeEvent<HTMLInputElement>,
					undefined,
					theme.version
				)
			}
		} else {
			for (i; i < colorFields.length; i++) {
				if (colorFields[i].userData && theme.colors[i]) {
					onChange(
						{target: {
							name: colorFields[i].userData, 
							value: theme.colors[i]
						}} as ChangeEvent<HTMLInputElement>,
						undefined,
						theme.version
					)
				}
			}
		}
	}, 500);

	// when we change content, or a version update happens, set displayed form values
	useEffect(() => {
		// when we change which content we're editing, clear the dirty, edited fields
		if (prevUniqueId !== uniqueId) {
			isDirty.current?.set(uniqueId, new Map());
		}

		// grab saved data for overlay (defaults will be used if no data is saved)
		const existingData = generatedContentItem?.data?.reduce((acc, curr) => {
			acc[curr.key] = curr.value;
			return acc;
		}, {} as Record<string, FieldValue>);

		const initialFormValues: Record<string, FieldValue> = {};
		// map over the api shape and determine the value for the form field
		selectedGraphicApiShape.forEach((field) => {
			if ((field?.variables?.length ?? 0) > 0 && InputTypeByCapsuleType[field.type] === 'picture') {
				const pictureCount = Number(existingData?.pictureCount ?? 1);
				initialFormValues.pictureCount = pictureCount.toString();
				for (let i = 0; i < pictureCount; i += 1) {
					const label = getNumberedFieldName(field.userData, i + 1);
					initialFormValues[label] = getField(existingData, field, label);

					field.variables.forEach((subField: any) => {
						const subLabel = getNumberedFieldName(subField.userData, i + 1);
						initialFormValues[subLabel] = getField(existingData, subField, subLabel);
					});
				}
			} else {
				const label = field.userData;
				initialFormValues[field.userData] = getField(existingData, field, label);
			}
		});

		if (!isNaN(Number(existingData.seconds))) {
			initialFormValues.seconds = Number(existingData.seconds);
		}

		// do not overwrite values the user has edited since opening this item
		const dirtyFields = isDirty.current?.get(uniqueId);
		dirtyFields?.forEach((value, key) => {
			initialFormValues[key] = value;
		});

		hardSetFormValuesRefAndUpdateStateValue(initialFormValues);
	}, [uniqueId, prevUniqueId, generatedContentItem.data]);

	const currrentThemeId = useMemo(() => {
		return generatedContentItem?.themeId ?? currTemplate?.themeId ?? 0;
	}, [generatedContentItem?.themeId, currTemplate?.themeId]);

	useEffect(() => {
		// if the theme has changed from outside intervention
		if (attachedThemes[uniqueId] !== currrentThemeId) {
			dispatch(attachTheme(uniqueId, currrentThemeId));
		}
	}, [attachedThemes, uniqueId, currrentThemeId]);

	return (canShow
		? <FormContext.Provider value={{
			statefulFormValues,
			selectedGraphicApiShape,
			currTemplate,
			UpdatingToaster,
			addNewPicture,
			removePictureWithCount,
			defaultTitleOverlayData,
			onDragEnd,
			onChange,
			uniqueId,
			hasText,
			applyThemeToItem,
			currrentThemeId,
			getTemplateSpecificStyling,
		}}>
			<ChipProvider 
				channelId={channelId ?? 0}
				contentId={generatedContentItem.id}
				onChange={onChange}
				uniqueId={uniqueId}
				forceShow={currTemplate.contentType === StylePackContentType.TITLE}
				isForShotEditorModal={props.isForShotEditorModal}
			>
				{props.children}
			</ChipProvider>
		</FormContext.Provider>
		: <></>
	);
}
